import { OpenInNew } from "@mui/icons-material"
import { Box, Chip, Link, Stack, Typography } from "@mui/material"
import type { FC, ReactNode } from "react"
import { Link as RouterLink } from "react-router-dom"

import { fmtAbsolute, fmtDuration, fmtRelative } from "./formatters"
import { SstSl2IdPair } from "./SstSl2IdPair"
import { getMuiTraceStatusColor } from "./traceColors"
import type { TraceItem } from "./types"

/**
 * Definition-list row inside a 2-column grid (`auto minmax(0, 1fr)`).
 * Labels stack vertically; ids span the wide value column without mid-string
 * wraps thanks to `overflowWrap: anywhere`.
 */
const DefRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        pt: 0.25,
        whiteSpace: "nowrap",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontSize: 10.5,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        fontFamily: "monospace",
        fontSize: 13,
        lineHeight: 1.35,
        overflowWrap: "anywhere",
        minWidth: 0,
      }}
    >
      {value}
    </Box>
  </>
)

const MetaDot: FC = () => (
  <Box
    sx={{
      width: 3,
      height: 3,
      borderRadius: "50%",
      bgcolor: "text.disabled",
    }}
  />
)

type Props = {
  trace: TraceItem
  xrayUrl: string
  rawPayloadSlot?: ReactNode
}

/**
 * Three-zone header for a single trace:
 *   1. Hero strip — event chip, status, relative time, prominent duration.
 *   2. Identifiers — Trace ID, X-Ray, Tenant, Reservation, Locker (label/value).
 *   3. Meta footer — started time · source · steps · errors.
 *
 * `rawPayloadSlot` lets the parent inject the raw-payload accordion at the
 * bottom of the card without this component needing to depend on JsonView.
 */
export const Sstv2TraceHeaderCard: FC<Props> = ({
  trace,
  xrayUrl,
  rawPayloadSlot,
}) => {
  return (
    <Box
      sx={{
        bgcolor: "white",
        border: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.08)",
        borderRadius: 2,
        p: { xs: 2, md: 2.5 },
        mb: 2,
      }}
    >
      {/* Zone 1 — Hero strip */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Chip
          size="small"
          label={trace.eventType}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          size="small"
          label={trace.status}
          color={getMuiTraceStatusColor(trace.status)}
        />
        <Typography variant="caption" color="text.secondary">
          {fmtRelative(trace.startedAt)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ textAlign: "right", lineHeight: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontSize: 10.5,
              lineHeight: 1,
            }}
          >
            duration
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.25 }}
          >
            {fmtDuration(trace.durationMs)}
          </Typography>
        </Box>
      </Stack>

      {/* Zone 2 — Identifiers, 2-col definition list */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          rowGap: 1.25,
          columnGap: 2.5,
          alignItems: "start",
        }}
      >
        <DefRow label="Trace ID" value={trace.traceId} />
        <DefRow
          label="X-Ray"
          value={
            trace.xrayRootId ? (
              <Link
                href={xrayUrl}
                target="_blank"
                rel="noreferrer noopener"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontFamily: "monospace",
                  fontSize: 13,
                }}
              >
                {trace.xrayRootId}
                <OpenInNew fontSize="inherit" />
              </Link>
            ) : (
              "-"
            )
          }
        />
        <DefRow
          label="Tenant"
          value={
            trace.tenantId ? (
              <Link
                component={RouterLink}
                to={`/sa/tenants/${encodeURIComponent(trace.tenantId)}`}
                sx={{ fontFamily: "monospace", fontSize: 13 }}
              >
                {trace.tenantId}
              </Link>
            ) : (
              "-"
            )
          }
        />
        <DefRow
          label="Reservation"
          value={
            <SstSl2IdPair
              kind="reservation"
              sstId={trace.sstReservationId}
              sl2Id={trace.reservationId}
            />
          }
        />
        <DefRow
          label="Locker"
          value={
            <SstSl2IdPair
              kind="locker"
              sstId={trace.sstLockerId}
              sl2Id={trace.lockerId}
            />
          }
        />
        {/* Migration traces only. The id is the whole run's handle, so the value
            doubles as a link back to every other event of the same migration. */}
        {trace.migrationId && (
          <DefRow
            label="Migration"
            value={
              <Link
                component={RouterLink}
                to={`/sa/sstv2?tab=6&migrationId=${encodeURIComponent(
                  trace.migrationId,
                )}`}
                sx={{ fontFamily: "monospace", fontSize: 13 }}
              >
                {trace.migrationId}
              </Link>
            }
          />
        )}
      </Box>

      {/* Zone 3 — Meta footer */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        divider={<MetaDot />}
        sx={{
          mt: 2,
          pt: 1.5,
          borderTop: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.06)",
          flexWrap: "wrap",
          rowGap: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          started {fmtAbsolute(trace.startedAt)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace" }}
        >
          {trace.source}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {trace.stepCount} step{trace.stepCount === 1 ? "" : "s"}
          {trace.errorCount > 0
            ? ` · ${trace.errorCount} error${
                trace.errorCount === 1 ? "" : "s"
              }`
            : ""}
        </Typography>
      </Stack>

      {rawPayloadSlot}
    </Box>
  )
}
